import { useCallback, type FC } from 'react';
import { useIntl } from 'react-intl';

import { ConditionalWrapper } from '@packages/shared/src/components/ConditionalWrapper/ConditionalWrapper';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SimpleFilterValueGridListFragmentFragmentDoc } from '@packages/gql/generated/shopping/SimpleFilterValueGridListFragmentFragmentDoc';

import { useProgressiveData } from '../../hooks/useProgressiveData';
import { bypassBlockMessages } from './messages';
import { SimpleFilterValueGridItem } from '../SimpleFilterValueGridItem/SimpleFilterValueGridItem';

/* GraphQL */ `
  fragment SimpleFilterValueGridListFragment on SearchDefaultFilter {
    groupedValues {
      groupName
      values {
        id
        ...SimpleFilterValueGridItemFragment
      }
    }
    displayName
  }
`;

export type SimpleFilterValueGridListProps = {
  maskedData: FragmentType<typeof SimpleFilterValueGridListFragmentFragmentDoc>;
  /** Currently selected filter value ids */
  selectedIds: string[];
  onToggle?: ({
    valueId,
    isSingleSelection,
  }: {
    valueId: string;
    isSingleSelection?: boolean;
  }) => void;
  /** Additional styles which should be applied on the container */
  containerClassName?: string;
};

/**
 * A list of multiple grids of simple filter value tiles, each with a header
 * */
export const SimpleFilterValueGridList: FC<SimpleFilterValueGridListProps> = ({
  maskedData,
  selectedIds,
  onToggle,
  containerClassName,
}) => {
  const { groupedValues, displayName } =
    unmask<typeof SimpleFilterValueGridListFragmentFragmentDoc>(maskedData);

  // Progressive rendering: show 1 group initially for better INP, then all groups after mount
  const visibleGroups = useProgressiveData(groupedValues, 1);

  const handleItemClick = useCallback(
    (valueId: string) => {
      onToggle?.({ valueId });
    },
    [onToggle],
  );

  const { formatMessage } = useIntl();

  const uniqueSelectedIds = new Set(selectedIds);

  return (
    <ul className={`mt-2 list-none overflow-y-auto ${containerClassName}`}>
      <BypassBlock
        buttonText={formatMessage(bypassBlockMessages.gridFilterValueList, {
          filterName: displayName,
        })}
        skipScrollIntoView
      >
        {visibleGroups.map((group) => (
          <ConditionalWrapper
            condition={group.values.length > 5}
            key={group.groupName}
            wrapper={(children) => (
              <BypassBlock
                buttonText={formatMessage(bypassBlockMessages.gridFilterValueGroup, {
                  filterGroupName: group.groupName,
                })}
                skipScrollIntoView
              >
                {children}
              </BypassBlock>
            )}
          >
            <>
              {group.groupName && (
                <li className="text-color-text-darktransparent bg-color-background-base sticky top-0 mb-1 px-2 pb-0.5">
                  {group.groupName}
                </li>
              )}
              <li className="mb-2 px-2 pb-0.5">
                <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(3.5rem,1fr))] items-center justify-between gap-1 p-0">
                  {group.values.map((value) => (
                    <SimpleFilterValueGridItem
                      key={value.id}
                      maskedData={value}
                      isSelected={uniqueSelectedIds.has(value.id)}
                      onClick={handleItemClick}
                    />
                  ))}
                </ul>
              </li>
            </>
          </ConditionalWrapper>
        ))}
      </BypassBlock>
    </ul>
  );
};
